import { useNavigate } from 'react-router-dom';
import { NutritionEssentialCard } from '@screens/HomePage/ResultReady/NutritionEssentialCard';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { BOOSTERS_AND_BLOCKERS_PATH } from '@/constants/paths';
import { useGetNutritionSummary } from '@viome/viome-base/src/usecase/Home/HomeQuery';
import { Skeleton } from '@design-system/Skeleton';
import {
  Carousel,
  CarouselNext,
  CarouselItem,
  CarouselContent,
  CarouselPrevious,
} from '@design-system/Carousel';
import { EventTracker } from '@/utils';

export const NutritionEssentials = () => {
  const {
    data: nutritionSummaryData,
    isLoading,
    isError,
  } = useGetNutritionSummary();

  const navigate = useNavigate();
  const handleSeeAll = () => {
    EventTracker.trackEvent('home', 'home_nutritionSummary_seeAll');
    navigate(BOOSTERS_AND_BLOCKERS_PATH);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 w-full">
        <div>
          <p className="mb-2 typography-h1 text-primary flex flex-row items-center gap-2">
            How our recommendations support you
          </p>
        </div>

        <div className="mt-8">
          <Carousel
            className="relative"
            opts={{
              align: 'start',
              dragFree: true
            }}
          >
            <CarouselContent className="-ml-6 gap-2">
              {[1, 2, 3, 4].map((index) => (
                <CarouselItem
                  key={`skeleton-${index}`}
                  className="pl-6 basis-80"
                >
                  <Skeleton className="w-80 h-28 rounded-2xl" />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden" />
            <CarouselNext className="hidden" />
          </Carousel>
        </div>

        <Skeleton className="w-full h-12 rounded-2xl" />
      </div>
    );
  }

  if (isError || !nutritionSummaryData?.length) {
    return;
  }

  return (
    <div className="space-y-6 w-full" data-testid="home-improveHealthSection">
      <div data-testid="home-improveHealthSection-header">
        <p className="mb-2 typography-h1 text-primary flex flex-row items-center gap-2" data-testid="home-improveHealthSection-title">
          How our recommendations support you
        </p>
      </div>

      <div className="mt-8" data-testid="home-improveHealthSection-carousel">
        <Carousel
          className="relative"
          opts={{
            align: 'start',
            dragFree: true,
          }}
        >
          <CarouselContent className="-ml-6 gap-2" data-testid="home-improveHealthSection-carouselContent">
            {nutritionSummaryData?.map((item, index) => (
              <CarouselItem
                key={`nutrition-${index}`}
                className="pl-6 basis-80"
                data-testid={`home-improveHealthSection-carouselItem-${index}`}
              >
                <NutritionEssentialCard
                  boosterBlockerId={Number(item.id)}
                  title={item.title}
                  images={item.image_urls}
                  foodCount={item.count}
                  icon_url={item.icon_url}
                  data-testid={`home-improveHealthSection-card-${item.title.replace(/\s+/g, '-').toLowerCase()}`}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden" />
          <CarouselNext className="hidden" />
        </Carousel>
      </div>

      <button
        onClick={handleSeeAll}
        className="w-full h-12 flex items-center justify-between px-6 bg-background-blur-bold backdrop-blur-[50px] rounded-2xl text-primary"
        data-testid="home-improveHealthSection-seeAllButton"
      >
        <span className="typography-btn-large">See all</span>
        <ChevronRightIcon className="w-5 h-5" />
      </button>
    </div>
  );
};
